import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Info as InfoIcon } from 'react-feather'
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core'

import { Collapse } from 'components'
import { IframePreview } from './components'

import useAuth from 'hooks/useAuth'
import useSnackbar from 'hooks/useSnackbar'

import theme from 'theme'
import useStyles from './styles'
import { getBaseURL } from 'service/env'
import constants from 'constants/index'

const IframeForm = ({ portalTheme }) => {
  const { MIN_ZOOM, MAX_ZOOM } = constants.privacyPortal
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { company } = useAuth()

  const [showCompanyInfo, setShowCompanyInfo] = useState(false)
  const [showPrivacyPolicies, setShowPrivacyPolicies] = useState(false)
  const [zoom, setZoom] = useState(100)

  const isExtraLarge = useMediaQuery(theme.breakpoints.up('lg'), {
    defaultMatches: true,
  })

  const companyToken = company?.token
  const complaintActivated = company?.complaintActivated

  const primaryColor = portalTheme?.primaryColor || theme.palette.primary.main
  const secondaryColor =
    portalTheme?.secondaryColor || theme.palette.secondary.main
  const secondaryFontColor =
    portalTheme?.secondaryFontColor || theme.palette.common.white
  const pageTitle = portalTheme?.pageTitle
  const pageSubtitle = portalTheme?.pageSubtitle

  const privacyPortalUrl = getBaseURL('privacyPortal')
  const urlParams = `${showCompanyInfo ? '' : '&hide_company_details=true'}${
    showPrivacyPolicies ? '' : '&hide_privacy_policies=true'
  }&zoom=${zoom}`
  const iframeUrl = `${privacyPortalUrl}/portal-de-privacidade/embed?token=${companyToken}${urlParams}`
  const iframeCode = `<iframe src="${iframeUrl}"
              width="100%"
              frameborder="0"
              scrolling="no"
              style="display: block; border: none; overflow: hidden; min-height: 100vh;"
>
</iframe>`

  const handleCopyIframeUrl = () => {
    navigator.clipboard.writeText(iframeUrl)
    snackbar.open({
      message: 'URL copiada para a área de transferência',
      variant: 'success',
    })
  }

  return (
    <Grid container spacing={isExtraLarge ? 10 : 6} py={6} pl={6}>
      <Grid item xs={12} lg={5}>
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(6)}>
          <Box
            id={constants.tours.privacyPortalTheme.IFRAME_STEP_1}
            display="flex"
            flexDirection="column"
            gridGap={theme.spacing(6)}
          >
            <Box
              display="flex"
              flexDirection="column"
              gridGap={theme.spacing(2)}
            >
              <Typography variant="h6">Configurações de exibição</Typography>
              <Box
                display="flex"
                flexDirection="column"
                gridGap={theme.spacing(2)}
              >
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={showCompanyInfo}
                      onChange={(event) =>
                        setShowCompanyInfo(event.target.checked)
                      }
                    />
                  }
                  label="Dados da empresa"
                />
                <Box
                  display="flex"
                  alignItems="center"
                  gridGap={theme.spacing(1)}
                >
                  <FormControlLabel
                    control={<Switch color="primary" checked disabled />}
                    label="Atendimento ao titular"
                  />
                  <Tooltip
                    title="Seção para permitir que o titular entre em contato com a empresa para exercer seus direitos ou tirar dúvidas sobre o tratamento de dados pessoais."
                    placement="right"
                  >
                    <IconButton size="small">
                      <InfoIcon size={16} color={theme.palette.info.main} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={showPrivacyPolicies}
                      onChange={(event) =>
                        setShowPrivacyPolicies(event.target.checked)
                      }
                    />
                  }
                  label="Documentos publicados"
                />
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              gridGap={theme.spacing(2)}
            >
              <Typography variant="h6">Zoom (%)</Typography>
              <TextField
                type="number"
                value={zoom}
                onChange={(event) => setZoom(Number(event.target.value))}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: MIN_ZOOM, max: MAX_ZOOM, step: 1 }}
              />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              gridGap={theme.spacing(4)}
            >
              <Typography variant="h6">Link de exibição</Typography>
              <TextField
                fullWidth
                multiline
                value={iframeUrl}
                InputLabelProps={{ shrink: true }}
                inputProps={{ readOnly: true }}
                InputProps={{
                  endAdornment: (
                    <Button variant="outlined" onClick={handleCopyIframeUrl}>
                      Copiar
                    </Button>
                  ),
                }}
              />
            </Box>
          </Box>
          <Box id={constants.tours.privacyPortalTheme.IFRAME_STEP_2}>
            <Collapse
              title={
                <Typography variant="body1">Ver exemplo de código</Typography>
              }
              defaultOpen={false}
              notBackgroundChildren
            >
              <Box className={classes.codeBox} p={2}>
                <Typography variant="caption" className={classes.codeText}>
                  {iframeCode}
                </Typography>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} lg={7}>
        <Box className={classes.previewContainer}>
          <IframePreview
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            secondaryTextColor={secondaryFontColor}
            pageTitle={pageTitle}
            pageSubtitle={pageSubtitle}
            showCompanyInfo={showCompanyInfo}
            showPrivacyPolicies={showPrivacyPolicies}
            complaintActivated={complaintActivated}
            zoom={Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom))}
          />
        </Box>
      </Grid>
    </Grid>
  )
}

IframeForm.propTypes = {
  portalTheme: PropTypes.object,
}

export default IframeForm
